import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { DevelopmentProjectType } from '../../utils/mockData'; // Adjust path as necessary
import {
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Chip,
  Stack,
  Divider,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  LocationOn,
  Business, // For Company Name
  Event, // For Launch Date
  Construction, // For Status (Under Construction)
  DoneAll, // For Status (Completed)
  Storefront, // For Status (Now Selling)
  Apartment // For Units Summary
} from '@mui/icons-material';

type DevelopmentProjectCardProps = {
  project: DevelopmentProjectType;
};

const DevelopmentProjectCard: React.FC<DevelopmentProjectCardProps> = ({ project }) => {
  const theme = useTheme();

  const getStatusIcon = (status: string) => {
    if (status.toLowerCase().includes('under construction')) return <Construction fontSize="small" sx={{ mr: 0.5 }} />;
    if (status.toLowerCase().includes('now selling')) return <Storefront fontSize="small" sx={{ mr: 0.5 }} />;
    if (status.toLowerCase().includes('completed')) return <DoneAll fontSize="small" sx={{ mr: 0.5 }} />;
    return <Apartment fontSize="small" sx={{ mr: 0.5 }} />; // Default icon
  };

  const getStatusColor = (status: string): "primary" | "secondary" | "error" | "warning" | "info" | "success" => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('now selling')) return 'success';
    if (lowerStatus.includes('under construction')) return 'warning';
    if (lowerStatus.includes('completed')) return 'primary';
    return 'info';
  };

  const availableUnits = project.units.filter(unit => unit.availability.toLowerCase() === 'available');
  const minPrice = availableUnits.length > 0
    ? Math.min(...availableUnits.map(unit => unit.price))
    : 0;

  const formattedMinPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(minPrice);

  const unitsSummary = availableUnits.length > 0
    ? `${availableUnits.length} unit${availableUnits.length > 1 ? 's' : ''} available from ${formattedMinPrice}`
    : `${project.units.length} unit${project.units.length > 1 ? 's' : ''} total`;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <CardActionArea component={RouterLink} to={`/development-projects/${project.id}`} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <CardMedia
          component="img"
          image={project.imageUrl}
          alt={project.projectName}
          sx={{
            height: 200,
            objectFit: 'cover',
          }}
        />

        {/* Status Chip */}
        <Chip
          label={project.status}
          color={getStatusColor(project.status)}
          size="small"
          icon={getStatusIcon(project.status)}
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 1,
            textTransform: 'capitalize',
            color: theme.palette.getContrastText(theme.palette[getStatusColor(project.status)].main)
          }}
        />

        <CardContent sx={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" component="h2" fontWeight={700} gutterBottom noWrap>
            {project.projectName}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, color: 'text.secondary' }}>
            <Business fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2" noWrap>
              {project.companyName}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, color: 'text.secondary' }}>
            <LocationOn fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2" noWrap>
              {project.location}
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Stack spacing={1} sx={{ mt: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <Event fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="body2">
                Launch: {project.launchDate}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <Apartment fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="body2">
                {unitsSummary}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default DevelopmentProjectCard;
